import { generatePath, type PathParam } from "react-router-dom";

/**
 * Utility type for better intellsense when using `react-router-dom#useParams`.
 * @example
 * const { organizationId } = useParams<RouteParams<RawRoute.organization>>();
 */
export type RouteParams<Path extends string> = { [Key in PathParam<Path>]: Key };

/** Calls generatePath as a curried function with type enforced parameters */
const generatePathWithParams =
  <Path extends string>(originalPath: Path) =>
  (params: Record<PathParam<Path>, string | null>) =>
    generatePath(originalPath, params);

/** Calls generatePath as a curried function with no parameters */
const generatePathNoParams =
  <Path extends string>(originalPath: Path) =>
  () =>
    generatePath(originalPath);

/* eslint-disable sort-keys-fix/sort-keys-fix */
/** This should be used sparingly as it provides little type safety when consuming routes */
export const RawRoute = {
  index: "/",

  // authentication
  login: "/login",
  loginCallback: "/login/google/callback",
  logout: "/logout",
  verify: "/verify",
  home: "/home",

  // admin
  admin: "/admin",
  adminSite: "/admin/site",
  adminSiteDataAnalytics: "/admin/site/platform-insights",
  adminSiteEntityManagement: "/admin/site/access-management",

  // organizations
  organizations: "/admin/organizations",
  myOrganizations: "/my-organizations",
  organization: "/organization/:organizationId",

  // public (unauthenticated) organization directory and landing pages
  publicDirectory: "/public",
  publicOrg: "/public/:slug",

  // profile
  profile: "/profile",
  profileCreate: "/profile/create",

  // rooms
  myRooms: "/my-rooms",
  podMembers: "/rooms/:roomId/members",
  podUpdate: "/rooms/:roomId/update",
  roomView: "/rooms/:roomId",
  podQr: "/rooms/:roomId/check-in-code",
  podCheckIn: "/rooms/:roomId/check-in",
  pods: "/pods",
  schedules: "/schedules",
  roomSearch: "/search/rooms",
  eventSearch: "/search/events",
  allSearch: "/search/rooms-and-events",
  /**
   * make payments for selected pod in redux
   */
  podPayment: "/pod/payment",

  // chats (rooms & events)
  platformChats: "/chats",

  // events
  myEvents: "/my-events",
  eventCreate: "/events/create",
  eventEdit: "/events/:eventId/update",
  eventMembers: "/events/:eventId/members",
  eventView: "/events/:eventId",
  eventQr: "/events/:eventId/check-in-code",
  eventCheckIn: "/events/:eventId/check-in",
  sessions: "/rooms/:roomId/sessions",

  // resources
  resourceLibrary: "/resource-library",

  /** Organization & admin tools (replaces sidebar Admin tree). */
  settings: "/settings",

  // bubbles
  bubbleQr: "/bubbles/:bubbleId/check-in-code",
  bubbleCheckIn: "/bubbles/:bubbleId/check-in",

  // notifications
  notifications: "/notifications",
  notificationsArchived: "/notifications/archived",

  // search
  searchCandidates: "/search/candidates",

  // users
  users: "/users",
  user: "/users/:userId",

  loginDistrict: "/login-district",

  /**
   * implements stripe refresh_url
   *
   * This must match server-side config StripePay::AccountCreationRefreshUrl
   * */
  profileRefresh: "/payment-refresh",

  // ai tutor
  aiTutor: "/ai-tutor",

  // tasks
  taskDashboard: "/tasks",

  /**
   * users are redirected here after successful payments
   *
   * This must match server-side config StripePay::PaymentSuccessUrl
   */
  paymentSuccess: "/payment-success",
  /**
   * users are redirected here after cancelling a payment
   *
   * This must match server-side config StripePay::PaymentCancelUrl
   */
  paymentCancel: "/payment-cancel",
  resumeBuilder: "/resume-builder",
  salesPortalContacts: "/sales-portal/contacts",
  salesPortalDealDetails: "/sales-portal/deals/:dealId",
  salesPortalDeals: "/sales-portal/deals",
  salesPortalHome: "/sales-portal/home",
  salesPortalPipeline: "/sales-portal/pipeline",
  salesPortalProspects: "/sales-portal/prospects",
  salesPortalProspectDetails: "/sales-portal/prospects/:prospectId",
  salesPortalTaskCalendar: "/sales-portal/task-calendar",
  salesPortalTemplates: "/sales-portal/templates",

  /** School-scoped visitor management (Front Office shell; no district nav) */
  schoolVisitorManagement: "/school/:schoolId/visitor-management",
  /** Day-of-emergency command center for a specific UTC calendar day (from declaration history, etc.) */
  schoolVisitorManagementReunificationDay: "/school/:schoolId/visitor-management/reunification/:dayOfEmergencyId",

  /**
   * Law enforcement reunification view (anonymous; token in path). Must match minted `PathHint` from the API
   * (`/emergency/day/{dayOfEmergencyId}/le/{token}`).
   *
   * Contract: access is per law-enforcement user per **day** (`dayOfEmergencyId`), not per emergency declaration.
   * Additional dispatches the same UTC day must reuse the same links/tokens; minting or invalidating tokens on each
   * new declaration breaks previously shared URLs (backend responsibility).
   *
   * Any LE email/SMS must include this URL whenever LE is notified (live or drill); see
   * `lawEnforcementEmergencyCommandCenterApi.ts` module doc (backend pipeline).
   */
  emergencyLawEnforcementCommandCenter: "/emergency/day/:dayOfEmergencyId/le/:token",
  /** Recipient / alarm-dismiss reunification view (anonymous; token in path). Matches API minted `PathHint` `/emergency/day/{id}/recipient/{token}`. */
  emergencyRecipientCommandCenter: "/emergency/day/:dayOfEmergencyId/recipient/:token",
  /**
   * Minimal anonymous page: report status + location only (schoolwide SMS quick link).
   * Matches API `PathHint` `/emergency/day/{dayOfEmergencyId}/quick-status/{token}`.
   */
  emergencyQuickStatusCheck: "/emergency/day/:dayOfEmergencyId/quick-status/:token",
} as const;
/* eslint-enable sort-keys-fix/sort-keys-fix */

/* eslint-disable sort-keys-fix/sort-keys-fix */
/** Returns type safe route that will inform the user when route parameters are required. */
export const Route = {
  index: generatePathNoParams(RawRoute.index),

  // authentication
  login: generatePathNoParams(RawRoute.login),
  loginCallback: generatePathNoParams(RawRoute.loginCallback),
  logout: generatePathNoParams(RawRoute.logout),
  verify: generatePathNoParams(RawRoute.verify),
  home: generatePathNoParams(RawRoute.home),

  // admin
  admin: generatePathNoParams(RawRoute.admin),
  adminSite: generatePathNoParams(RawRoute.adminSite),
  adminSiteDataAnalytics: generatePathNoParams(RawRoute.adminSiteDataAnalytics),
  adminSiteEntityManagement: generatePathNoParams(RawRoute.adminSiteEntityManagement),

  // organizations
  organizations: generatePathNoParams(RawRoute.organizations),
  myOrganizations: generatePathNoParams(RawRoute.myOrganizations),
  organization: generatePathWithParams(RawRoute.organization),
  publicDirectory: generatePathNoParams(RawRoute.publicDirectory),
  publicOrg: generatePathWithParams(RawRoute.publicOrg),

  // profile
  profile: generatePathNoParams(RawRoute.profile),
  profileCreate: generatePathNoParams(RawRoute.profileCreate),

  aiTutor: generatePathNoParams(RawRoute.aiTutor),

  // tasks
  taskDashboard: generatePathNoParams(RawRoute.taskDashboard),

  platformChats: generatePathNoParams(RawRoute.platformChats),

  // events
  myEvents: generatePathNoParams(RawRoute.myEvents),
  eventCreate: generatePathNoParams(RawRoute.eventCreate),
  eventEdit: generatePathWithParams(RawRoute.eventEdit),
  eventMembers: generatePathWithParams(RawRoute.eventMembers),
  eventView: generatePathWithParams(RawRoute.eventView),
  eventQr: generatePathWithParams(RawRoute.eventQr),
  eventCheckIn: generatePathWithParams(RawRoute.eventCheckIn),
  sessions: generatePathWithParams(RawRoute.sessions),

  // resources
  resourceLibrary: generatePathNoParams(RawRoute.resourceLibrary),

  settings: generatePathNoParams(RawRoute.settings),

  // rooms
  myRooms: generatePathNoParams(RawRoute.myRooms),
  podMembers: generatePathWithParams(RawRoute.podMembers),
  podUpdate: generatePathWithParams(RawRoute.podUpdate),
  roomView: generatePathWithParams(RawRoute.roomView),
  podQr: generatePathWithParams(RawRoute.podQr),
  podCheckIn: generatePathWithParams(RawRoute.podCheckIn),
  pods: generatePathNoParams(RawRoute.pods),
  schedules: generatePathNoParams(RawRoute.schedules),
  roomSearch: generatePathNoParams(RawRoute.roomSearch),
  eventSearch: generatePathNoParams(RawRoute.eventSearch),
  allSearch: generatePathNoParams(RawRoute.allSearch),
  /**
   * make payments for selected pod in redux
   */
  podPayment: generatePathNoParams(RawRoute.podPayment),

  // bubbles
  bubbleQr: generatePathWithParams(RawRoute.bubbleQr),
  bubbleCheckIn: generatePathWithParams(RawRoute.bubbleCheckIn),

  // notifications
  notifications: generatePathNoParams(RawRoute.notifications),
  notificationsArchived: generatePathNoParams(RawRoute.notificationsArchived),

  loginDistrict: generatePathNoParams(RawRoute.loginDistrict),

  // users
  users: generatePathNoParams(RawRoute.users),
  user: generatePathWithParams(RawRoute.user),
  /**
   * implements stripe refresh_url
   *
   * This must match server-side config StripePay::AccountCreationRefreshUrl
   * */
  profileRefresh: generatePathNoParams(RawRoute.profileRefresh),
  /**
   * users are redirected here after successful payments
   *
   * This must match server-side config StripePay::PaymentSuccessUrl
   */
  paymentSuccess: generatePathNoParams(RawRoute.paymentSuccess),
  /**
   * users are redirected here after cancelling a payment
   *
   * This must match server-side config StripePay::PaymentCancelUrl
   */
  paymentCancel: generatePathNoParams(RawRoute.paymentCancel),
  resumeBuilder: generatePathNoParams(RawRoute.resumeBuilder),
  salesPortalContacts: generatePathNoParams(RawRoute.salesPortalContacts),
  salesPortalDealDetails: generatePathWithParams(RawRoute.salesPortalDealDetails),
  salesPortalDeals: generatePathNoParams(RawRoute.salesPortalDeals),
  salesPortalHome: generatePathNoParams(RawRoute.salesPortalHome),
  salesPortalPipeline: generatePathNoParams(RawRoute.salesPortalPipeline),
  salesPortalProspects: generatePathNoParams(RawRoute.salesPortalProspects),
  salesPortalProspectDetails: generatePathWithParams("/sales-portal/prospects/:prospectId"),
  salesPortalTaskCalendar: generatePathNoParams(RawRoute.salesPortalTaskCalendar),
  salesPortalTemplates: generatePathNoParams(RawRoute.salesPortalTemplates),

  // search
  searchCandidates: generatePathNoParams(RawRoute.searchCandidates),

  schoolVisitorManagement: generatePathWithParams(RawRoute.schoolVisitorManagement),
  schoolVisitorManagementReunificationDay: generatePathWithParams(RawRoute.schoolVisitorManagementReunificationDay),
  emergencyLawEnforcementCommandCenter: generatePathWithParams(RawRoute.emergencyLawEnforcementCommandCenter),
  emergencyRecipientCommandCenter: generatePathWithParams(RawRoute.emergencyRecipientCommandCenter),
  emergencyQuickStatusCheck: generatePathWithParams(RawRoute.emergencyQuickStatusCheck),
};
/* eslint-enable sort-keys-fix/sort-keys-fix */
